"use client";
/* eslint-disable @next/next/no-html-link-for-pages, @next/next/no-img-element */

import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type {
  ApiEnvelope,
  MonthlyPerformance,
  PublicAnalytics,
  PublicEquity,
  PublicSummary,
} from "../../contracts/src/index.ts";
import { PUBLIC_EQUITY_ALL_HISTORY_FROM } from "../../contracts/src/index.ts";
import { AccountChart, StrategyContributionChart } from "./AccountChart.tsx";
import { getCanonicalAccountMetrics } from "./account-metrics.ts";
import type { ChartMode, PeriodKey } from "./chart-model.ts";
import {
  formatDate,
  formatMoney,
  formatPercent,
  MonthlyBarChart,
  PerformanceHeatmap,
  UnderwaterChart,
} from "./charts.tsx";
import {
  productCopy,
  siteConfig,
  strategyDisplayName,
  strategyModules,
  validEmail,
  validHttpsUrl,
} from "./config/site.ts";
import { ErrorBoundary } from "./ErrorBoundary.tsx";
import { getFreshness } from "./freshness.ts";
import {
  readLastKnownGoodAnalytics,
  readLastKnownGoodEquityOverview,
  writeLastKnownGoodAnalytics,
  writeLastKnownGoodEquityOverview,
} from "./public-dashboard-cache.ts";
import {
  readLastKnownGoodSummary,
  writeLastKnownGoodSummary,
} from "./public-summary-cache.ts";

export type SiteView =
  | "home"
  | "performance"
  | "verification"
  | "privacy"
  | "terms"
  | "risk"
  | "changelog"
  | "status"
  | "not-found";

interface DashboardProps {
  apiBaseUrl?: string;
  accountId?: string;
  view?: SiteView;
}

const periodOptions: Array<{
  value: PeriodKey;
  label: string;
  days: number | null;
}> = [
  { value: "1m", label: "1 個月", days: 30 },
  { value: "3m", label: "3 個月", days: 90 },
  { value: "6m", label: "6 個月", days: 180 },
  { value: "1y", label: "1 年", days: 365 },
  { value: "all", label: "全部", days: null },
];
const validPeriods = new Set<PeriodKey>(
  periodOptions.map((option) => option.value),
);
const SIGNALSTART_URL =
  "https://www.signalstart.com/analysis/amazing-system-7275/289129";
const MYFXBOOK_URL =
  "https://www.myfxbook.com/portfolio/million-profit-robot/12038054";
const URL_STATE_EVENT = "hq:urlstatechange";

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, "")}${path}`;
}

async function getJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    signal,
    headers: { accept: "application/json" },
  });
  const payload = (await response.json()) as ApiEnvelope<T>;
  if (!response.ok || !payload.ok || payload.data === undefined) {
    throw new Error(payload.error?.message ?? `HTTP ${response.status}`);
  }
  return payload.data;
}

const summaryRequestsInFlight = new Map<string, Promise<PublicSummary>>();
const equityRequestsInFlight = new Map<string, Promise<PublicEquity>>();
const analyticsRequestsInFlight = new Map<string, Promise<PublicAnalytics>>();

function sharedJson<T>(cache: Map<string, Promise<T>>, url: string): Promise<T> {
  const existing = cache.get(url);
  if (existing) return existing;
  const controller = new AbortController();
  const request = getJson<T>(url, controller.signal);
  cache.set(url, request);
  void request.then(
    () => {
      if (cache.get(url) === request) cache.delete(url);
    },
    () => {
      if (cache.get(url) === request) cache.delete(url);
    },
  );
  return request;
}

function formatTaipeiTime(timestamp: number | null | undefined): string {
  if (!timestamp) return "N/A";
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(timestamp * 1000);
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds))
    return "N/A";
  if (seconds < 3600) return `${Math.round(seconds / 60)} 分鐘`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} 小時`;
  return `${(seconds / 86400).toFixed(1)} 天`;
}

function safeUrl(value: string): string | null {
  return validHttpsUrl(value);
}

function readSearch(): URLSearchParams {
  return typeof window === "undefined"
    ? new URLSearchParams()
    : new URLSearchParams(window.location.search);
}

function replaceSearch(updates: Record<string, string | null>, push = false) {
  if (typeof window === "undefined") return;
  const search = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(updates)) {
    if (!value) search.delete(key);
    else search.set(key, value);
  }
  const query = search.toString();
  const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  window.history[push ? "pushState" : "replaceState"]({}, "", next);
  window.dispatchEvent(new Event(URL_STATE_EVENT));
}

function routeHref(
  path: string,
  keys: string[] = [],
  currentSearch = "",
): string {
  if (keys.length === 0 || currentSearch === "") return path;
  const current = new URLSearchParams(currentSearch);
  const next = new URLSearchParams();
  keys.forEach((key) => {
    const value = current.get(key);
    if (value) next.set(key, value);
  });
  return `${path}${next.size ? `?${next}` : ""}`;
}

function Icon({
  name,
}: {
  name:
    | "home"
    | "performance"
    | "more"
    | "shield"
    | "arrow"
    | "moon"
    | "sun"
    | "external"
    | "database";
}) {
  const paths = {
    home: (
      <>
        <path d="m3 11 9-8 9 8" />
        <path d="M5 10v10h14V10M9 20v-6h6v6" />
      </>
    ),
    performance: (
      <>
        <path d="M4 19V5M4 19h16" />
        <path d="m7 15 4-4 3 2 5-6" />
      </>
    ),
    more: (
      <>
        <circle cx="5" cy="12" r="1" />
        <circle cx="12" cy="12" r="1" />
        <circle cx="19" cy="12" r="1" />
      </>
    ),
    shield: (
      <path d="M12 3 4.5 6v5.5c0 4.6 3.1 7.7 7.5 9.5 4.4-1.8 7.5-4.9 7.5-9.5V6L12 3Z" />
    ),
    arrow: (
      <>
        <path d="M5 12h14" />
        <path d="m14 7 5 5-5 5" />
      </>
    ),
    moon: <path d="M21 13.4A8.5 8.5 0 1 1 10.6 3a7 7 0 0 0 10.4 10.4Z" />,
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2" />
      </>
    ),
    external: (
      <>
        <path d="M14 4h6v6M20 4l-9 9" />
        <path d="M18 13v6H5V6h6" />
      </>
    ),
    database: (
      <>
        <ellipse cx="12" cy="5" rx="8" ry="3" />
        <path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7" />
      </>
    ),
  };
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function useTheme() {
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  useEffect(() => {
    const saved = window.localStorage.getItem("hq-theme");
    const next = saved === "light" ? "light" : "dark";
    queueMicrotask(() => setTheme(next));
    document.documentElement.dataset.theme = next;
  }, []);
  const toggle = () =>
    setTheme((current) => {
      const next = current === "dark" ? "light" : "dark";
      document.documentElement.dataset.theme = next;
      window.localStorage.setItem("hq-theme", next);
      return next;
    });
  return { theme, toggle };
}

function useClockSeconds(): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    const update = () => setNow(Math.floor(Date.now() / 1000));
    const initial = window.setTimeout(update, 0);
    const interval = window.setInterval(update, 60_000);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
    };
  }, []);
  return now;
}

function useCurrentSearch(): string {
  const [currentSearch, setCurrentSearch] = useState("");
  useEffect(() => {
    const sync = () => setCurrentSearch(window.location.search);
    queueMicrotask(sync);
    window.addEventListener("popstate", sync);
    window.addEventListener(URL_STATE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(URL_STATE_EVENT, sync);
    };
  }, []);
  return currentSearch;
}

function Logo() {
  return (
    <a className="brand" href="/">
      <img src="/hybrid-quant-mark.svg" alt="" width="44" height="44" />
      <span>
        <strong>{siteConfig.eaName}</strong>
        <small>PUBLIC PERFORMANCE</small>
      </span>
    </a>
  );
}

const primaryRoutes: Array<{ path: string; label: string; view: SiteView }> = [
  { path: "/", label: "總覽", view: "home" },
  { path: "/performance", label: "完整績效", view: "performance" },
  { path: "/verification", label: "公開紀錄", view: "verification" },
];

function stateKeysForView(view: SiteView): string[] {
  if (view === "performance")
    return ["section", "strategy", "period", "chartMode"];
  return [];
}

function Header({
  view,
  theme,
  onTheme,
  currentSearch,
}: {
  view: SiteView;
  theme: "dark" | "light";
  onTheme: () => void;
  currentSearch: string;
}) {
  return (
    <header className="site-header">
      <div className="site-container header-inner">
        <Logo />
        <nav aria-label="主要導覽">
          {primaryRoutes.map((route) => (
            <a
              key={route.path}
              className={view === route.view ? "active" : ""}
              href={routeHref(
                route.path,
                stateKeysForView(route.view),
                currentSearch,
              )}
            >
              {route.label}
            </a>
          ))}
        </nav>
        <div className="header-actions">
          <button
            className="icon-button"
            type="button"
            onClick={onTheme}
            aria-label="切換顯示模式"
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
          <a className="button small" href="/#contact">
            FAQ／聯絡
          </a>
        </div>
      </div>
    </header>
  );
}

function MobileNavigation({
  view,
  currentSearch,
}: {
  view: SiteView;
  currentSearch: string;
}) {
  const items = primaryRoutes;
  return (
    <nav className="mobile-bottom-nav" aria-label="手機導覽">
      {items.map((route) => (
        <a
          key={route.path}
          className={view === route.view ? "active" : ""}
          href={routeHref(
            route.path,
            stateKeysForView(route.view),
            currentSearch,
          )}
        >
          <Icon
            name={
              route.view === "home"
                ? "home"
                : route.view === "performance"
                  ? "performance"
                  : "shield"
            }
          />
          <span>{route.label}</span>
        </a>
      ))}
      <details className="mobile-more">
        <summary>
          <Icon name="more" />
          <span>更多</span>
        </summary>
        <div>
          <a href="/#contact">FAQ／聯絡</a>
          <a href="/status">系統狀態</a>
          <a href="/changelog">版本紀錄</a>
          <a href="/risk-disclosure">風險揭露</a>
        </div>
      </details>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-container footer-grid">
        <div>
          <Logo />
          <p>{siteConfig.tagline}</p>
        </div>
        <div>
          <strong>公開資料</strong>
          <a href="/performance">完整績效</a>
          <a href="/performance?section=monthly">月度績效</a>
          <a href="/performance?section=strategies&amp;strategy=ALL">策略分析</a>
          <a href="/verification">公開紀錄</a>
        </div>
        <div>
          <strong>法律與系統</strong>
          <a href="/privacy">隱私權政策</a>
          <a href="/terms">使用條款</a>
          <a href="/risk-disclosure">風險揭露</a>
          <a href="/status">系統狀態</a>
        </div>
        <div>
          <strong>版本</strong>
          <span>Website {siteConfig.version}</span>
          <span>Data contract {siteConfig.dataVersion}</span>
          <span>Updated {siteConfig.updatedAt}</span>
        </div>
      </div>
      <div className="site-container footer-bottom">
        <span>
          © {new Date().getUTCFullYear()} {siteConfig.brandName}.
        </span>
        <span>歷史績效不代表未來結果。</span>
      </div>
    </footer>
  );
}

function PageShell({
  view,
  children,
}: {
  view: SiteView;
  children: ReactNode;
}) {
  const { theme, toggle } = useTheme();
  const currentSearch = useCurrentSearch();
  return (
    <div className="site-shell">
      <Header
        view={view}
        theme={theme}
        onTheme={toggle}
        currentSearch={currentSearch}
      />
      {children}
      <Footer />
      <MobileNavigation view={view} currentSearch={currentSearch} />
    </div>
  );
}

function SectionHeading({
  kicker,
  title,
  copy,
}: {
  kicker: string;
  title: string;
  copy?: string;
}) {
  return (
    <div className="section-heading">
      <p className="eyebrow">{kicker}</p>
      <h1>{title}</h1>
      {copy ? <p>{copy}</p> : null}
    </div>
  );
}

function MetricCard({
  label,
  value,
  note,
  tone = "default",
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "default" | "positive" | "risk";
}) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <small>{note}</small> : null}
    </article>
  );
}

function StatusPill({
  level,
  label,
}: {
  level: "live" | "delayed" | "offline" | "neutral";
  label: string;
}) {
  return (
    <span className={`status-pill ${level}`}>
      <i />
      {label}
    </span>
  );
}

function useAccountData(
  apiBaseUrl: string,
  accountId: string,
  period: PeriodKey,
  enabled = true,
) {
  const [summary, setSummary] = useState<PublicSummary | null>(null);
  const [equity, setEquity] = useState<PublicEquity | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [equityLoading, setEquityLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [equityError, setEquityError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const option =
      periodOptions.find((item) => item.value === period) ?? periodOptions[3];
    const from = option.days
      ? Math.floor(Date.now() / 1000) - option.days * 86400
      : PUBLIC_EQUITY_ALL_HISTORY_FROM;
    const accountQuery = encodeURIComponent(accountId);
    const summaryUrl = joinUrl(
      apiBaseUrl,
      `/api/v1/public/summary?account_id=${accountQuery}`,
    );
    const equityUrl = joinUrl(
      apiBaseUrl,
      `/api/v1/public/equity?account_id=${accountQuery}&from=${from}&resolution=overview`,
    );
    let lastRefreshAt = 0;

    const refreshSummary = async () => {
      try {
        const nextSummary = await sharedJson(
          summaryRequestsInFlight,
          summaryUrl,
        );
        if (!cancelled) {
          setSummary(nextSummary);
          setSummaryError(null);
          writeLastKnownGoodSummary(
            window.localStorage,
            apiBaseUrl,
            accountId,
            nextSummary,
          );
        }
      } catch (caught) {
        if (!cancelled)
          setSummaryError(
            caught instanceof Error ? caught.message : "公開資料暫時無法載入",
          );
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };

    const refreshEquity = async () => {
      try {
        const nextEquity = await sharedJson(equityRequestsInFlight, equityUrl);
        if (!cancelled) {
          setEquity(nextEquity);
          setEquityError(null);
          writeLastKnownGoodEquityOverview(
            window.localStorage,
            apiBaseUrl,
            accountId,
            period,
            nextEquity,
          );
        }
      } catch (caught) {
        if (!cancelled)
          setEquityError(
            caught instanceof Error ? caught.message : "績效曲線暫時無法載入",
          );
      } finally {
        if (!cancelled) setEquityLoading(false);
      }
    };

    const refresh = () => {
      lastRefreshAt = Date.now();
      void refreshSummary();
      void refreshEquity();
    };
    const refreshOnResume = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastRefreshAt >= 1_000
      ) {
        refresh();
      }
    };

    const cachedSummary = readLastKnownGoodSummary(
      window.localStorage,
      apiBaseUrl,
      accountId,
    );
    if (cachedSummary) {
      queueMicrotask(() => {
        if (!cancelled) {
          setSummary(cachedSummary);
          setSummaryLoading(false);
        }
      });
    }
    const cachedEquity = readLastKnownGoodEquityOverview(
      window.localStorage,
      apiBaseUrl,
      accountId,
      period,
    );
    if (cachedEquity) {
      queueMicrotask(() => {
        if (!cancelled) {
          setEquity(cachedEquity);
          setEquityLoading(false);
        }
      });
    }
    refresh();
    document.addEventListener("visibilitychange", refreshOnResume);
    window.addEventListener("focus", refreshOnResume);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", refreshOnResume);
      window.removeEventListener("focus", refreshOnResume);
    };
  }, [apiBaseUrl, accountId, period, enabled]);
  return {
    summary,
    equity,
    summaryLoading,
    equityLoading,
    error: summaryError ?? equityError,
  };
}

function useAnalytics(
  apiBaseUrl: string,
  accountId: string,
  strategy = "ALL",
  enabled = true,
) {
  const [analytics, setAnalytics] = useState<PublicAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const query = new URLSearchParams({ account_id: accountId, strategy });
    const url = joinUrl(apiBaseUrl, `/api/v1/public/analytics?${query}`);
    let lastRefreshAt = 0;
    const refresh = async () => {
      lastRefreshAt = Date.now();
      try {
        const next = await sharedJson(analyticsRequestsInFlight, url);
        if (!cancelled) {
          setAnalytics(next);
          setError(null);
          writeLastKnownGoodAnalytics(
            window.localStorage,
            apiBaseUrl,
            accountId,
            strategy,
            next,
          );
        }
      } catch (caught) {
        if (!cancelled)
          setError(
            caught instanceof Error ? caught.message : "分析資料暫時無法載入",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const refreshOnResume = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastRefreshAt >= 1_000
      ) {
        void refresh();
      }
    };

    const cachedAnalytics = readLastKnownGoodAnalytics(
      window.localStorage,
      apiBaseUrl,
      accountId,
      strategy,
    );
    if (cachedAnalytics) {
      queueMicrotask(() => {
        if (!cancelled) {
          setAnalytics(cachedAnalytics);
          setLoading(false);
        }
      });
    }
    void refresh();
    document.addEventListener("visibilitychange", refreshOnResume);
    window.addEventListener("focus", refreshOnResume);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", refreshOnResume);
      window.removeEventListener("focus", refreshOnResume);
    };
  }, [apiBaseUrl, accountId, strategy, enabled]);
  return { analytics, loading, error };
}

type PerformanceSection = "overview" | "monthly" | "strategies" | "risk";

interface PerformanceState {
  section: PerformanceSection;
  strategy: string;
  period: PeriodKey;
  chartMode: ChartMode;
}

function parsePerformanceState(
  search = readSearch(),
): PerformanceState {
  const sectionValue = search.get("section");
  const strategyValue = search.get("strategy") ?? "ALL";
  const periodValue = search.get("period")?.toLowerCase() as
    | PeriodKey
    | undefined;
  return {
    section:
      sectionValue === "monthly" ||
      sectionValue === "strategies" ||
      sectionValue === "risk"
        ? sectionValue
        : "overview",
    strategy: /^(ALL|\d{1,24})$/.test(strategyValue)
      ? strategyValue
      : "ALL",
    period:
      periodValue && validPeriods.has(periodValue) ? periodValue : "1y",
    chartMode:
      search.get("chartMode") === "amount" ||
      (sectionValue === "risk" && !search.has("chartMode"))
        ? "amount"
        : "performance",
  };
}

function usePerformanceState() {
  const [state, setState] = useState<PerformanceState>(() =>
    parsePerformanceState(new URLSearchParams()),
  );
  useEffect(() => {
    const sync = () => setState(parsePerformanceState());
    queueMicrotask(sync);
    window.addEventListener("popstate", sync);
    window.addEventListener(URL_STATE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(URL_STATE_EVENT, sync);
    };
  }, []);
  const update = useCallback((patch: Partial<PerformanceState>) => {
    const next = { ...parsePerformanceState(), ...patch };
    setState(next);
    replaceSearch({
      section: next.section,
      strategy: next.strategy,
      period: next.period,
      chartMode: next.chartMode,
    });
  }, []);
  return { state, update };
}

function DataAlert({ message }: { message: string | null }) {
  return message ? (
    <div className="data-alert" role="status">
      <strong>資料暫時無法載入</strong>
      <span>{message}</span>
    </div>
  ) : null;
}

function PeriodTabs({
  value,
  onChange,
}: {
  value: PeriodKey;
  onChange: (next: PeriodKey) => void;
}) {
  return (
    <div className="range-tabs" role="group" aria-label="時間範圍">
      {periodOptions.map((option) => (
        <button
          type="button"
          key={option.value}
          className={value === option.value ? "active" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ProductContactCta() {
  const email = validEmail(siteConfig.contactEmail);
  const links = [
    ["LINE", safeUrl(siteConfig.lineUrl)],
    ["Telegram", safeUrl(siteConfig.telegramUrl)],
    ["Instagram", safeUrl(siteConfig.instagramUrl)],
  ] as const;
  return (
    <section className="home-cta" id="contact">
      <div>
        <p className="eyebrow">MT5 EXPERT ADVISOR</p>
        <h2>先談風險與適用條件，再談工具</h2>
        <p>
          Hybrid Quant EA 參考售價{" "}
          {new Intl.NumberFormat("zh-TW", {
            style: "currency",
            currency: siteConfig.currency,
            maximumFractionDigits: 0,
          }).format(siteConfig.price)}
          。{siteConfig.priceNote}
        </p>
      </div>
      <div className="cta-links">
        {links.map(([label, href]) =>
          href ? (
            <a key={label} href={href} target="_blank" rel="noreferrer">
              {label}
              <Icon name="external" />
            </a>
          ) : null,
        )}
        {email ? <a href={`mailto:${email}`}>Email</a> : null}
        {!links.some(([, href]) => href) && !email ? (
          <span>聯絡資料尚未設定</span>
        ) : null}
      </div>
    </section>
  );
}

function HomePage({
  apiBaseUrl,
  accountId,
}: {
  apiBaseUrl: string;
  accountId: string;
}) {
  const { summary, equity, summaryLoading, equityLoading, error } = useAccountData(
    apiBaseUrl,
    accountId,
    "1y",
  );
  const accountAnalytics = useAnalytics(apiBaseUrl, accountId, "ALL");
  const metrics = getCanonicalAccountMetrics(summary, accountAnalytics.analytics);
  const now = useClockSeconds();
  const freshness =
    summary && now ? getFreshness(summary.lastUpdatedAt, now) : null;
  return (
    <PageShell view="home">
      <main>
        <section className="hero-section">
          <div className="site-container hero-grid">
            <div className="hero-copy-block">
              <p className="eyebrow">SYSTEMATIC EXECUTION · VISIBLE RISK</p>
              <h1>
                讓策略依數據執行，
                <br />
                <em>讓風險由紀律控制</em>
              </h1>
              <p className="hero-lead">
                以趨勢、突破、動能、波動調節與組合風控共同決策的 MT5
                自動交易系統，公開呈現實際運作結果。
              </p>
              <div className="hero-actions">
                <a className="button primary" href="/performance">
                  前往完整績效 <Icon name="arrow" />
                </a>
                <a className="text-link" href="#contact">
                  聯絡諮詢
                </a>
              </div>
              <div className="hero-proof">
                <span>
                  <Icon name="shield" />
                  非無上限馬丁／網格
                </span>
                <span>
                  <Icon name="database" />
                  現金流中性績效
                </span>
              </div>
            </div>
            <div className="hero-visual-stack">
              <div
                className="hero-animation"
                aria-label="Hybrid Quant 多訊號策略動畫"
              >
                <div className="quant-orbit orbit-one" />
                <div className="quant-orbit orbit-two" />
                <span className="signal-node node-trend">Trend</span>
                <span className="signal-node node-momentum">Momentum</span>
                <span className="signal-node node-volatility">Volatility</span>
                <span className="signal-node node-risk">Risk Gate</span>
                <div className="quant-core">
                  <img
                    src="/hybrid-quant-mark-dark.svg"
                    alt="Hybrid Quant EA"
                  />
                  <strong>
                    HYBRID
                    <br />
                    QUANT
                  </strong>
                  <small>MULTI-SIGNAL ENGINE</small>
                </div>
              </div>
              <div className="hero-live-card">
                <div>
                  {freshness ? (
                    <StatusPill
                      level={freshness.level}
                      label={freshness.label}
                    />
                  ) : (
                    <StatusPill level="neutral" label="Waiting" />
                  )}
                  <span>{summary?.accountType ?? "N/A"}</span>
                </div>
                <strong>
                  {formatMoney(summary?.equity, summary?.currency ?? "USD")}
                </strong>
                <small>
                  LIVE EQUITY · {formatTaipeiTime(summary?.lastUpdatedAt)}
                </small>
              </div>
            </div>
          </div>
        </section>
        <section className="overview-section">
          <div className="site-container">
            <DataAlert message={error ?? accountAnalytics.error} />
            <div className="metrics-grid home-metrics">
              {(summaryLoading || accountAnalytics.loading) && (!summary || !metrics) ? (
                Array.from({ length: 7 }, (_, index) => (
                  <div className="metric-card skeleton" key={index} />
                ))
              ) : (
                <>
                  <MetricCard
                    label="Balance"
                    value={formatMoney(
                      metrics?.balance,
                      summary?.currency ?? "USD",
                    )}
                  />
                  <MetricCard
                    label="Equity"
                    value={formatMoney(
                      metrics?.equity,
                      summary?.currency ?? "USD",
                    )}
                  />
                  <MetricCard
                    label="累積現金流中性報酬"
                    value={formatPercent(metrics?.cumulativeReturnPct, true)}
                    tone="positive"
                  />
                  <MetricCard
                    label="本月報酬"
                    value={formatPercent(metrics?.currentMonthReturnPct, true)}
                  />
                  <MetricCard
                    label="目前回撤"
                    value={formatPercent(metrics?.currentDrawdownPct)}
                    tone="risk"
                  />
                  <MetricCard
                    label="最大回撤"
                    value={formatPercent(metrics?.maximumDrawdownPct)}
                    tone="risk"
                  />
                  <MetricCard
                    label="帳戶狀態"
                    value={
                      summary?.accountType === "REAL"
                        ? "Real Account"
                        : (summary?.accountType ?? "N/A")
                    }
                    note={`最後同步 ${formatTaipeiTime(summary?.lastUpdatedAt)}`}
                  />
                </>
              )}
            </div>
            <article className="panel home-chart">
              <div className="panel-head">
                <div>
                  <h2>精簡績效曲線</h2>
                  <p>現金流中性；入金與出金不會被視為交易報酬。</p>
                </div>
                <a
                  className="panel-link"
                  href="/performance?period=1y&chartMode=performance"
                >
                  查看完整圖表 <Icon name="arrow" />
                </a>
              </div>
              {equityLoading && !equity ? (
                <div className="chart-loading" />
              ) : (
                <AccountChart
                  equity={equity}
                  summary={summary}
                  currency={summary?.currency ?? "USD"}
                  mode="performance"
                  period="1y"
                  compact
                />
              )}
            </article>
          </div>
        </section>
        <section className="home-intro">
          <div className="site-container">
            <SectionHeading
              kicker="HYBRID QUANT"
              title="多層條件共同決策"
              copy={productCopy.short}
            />
            <div className="strategy-mini-grid">
              {strategyModules.slice(0, 4).map(([number, title, copy]) => (
                <article key={number}>
                  <span>{number}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
            <div className="public-record-links">
              <a href={SIGNALSTART_URL} target="_blank" rel="noreferrer">
                <strong>SignalStart 公開績效頁</strong>
                <span>已連結公開紀錄</span>
                <Icon name="external" />
              </a>
              <a href={MYFXBOOK_URL} target="_blank" rel="noreferrer">
                <strong>Myfxbook 公開績效頁</strong>
                <span>第三方公開追蹤頁</span>
                <Icon name="external" />
              </a>
            </div>
            <ProductContactCta />
          </div>
        </section>
      </main>
    </PageShell>
  );
}

function statusLabel(month: MonthlyPerformance): string {
  if (month.status === "PARTIAL_INCEPTION") return "部分月份";
  if (month.status === "MTD") return "本月至今";
  if (month.status === "COMPLETE") return "完整月份";
  return month.status.replaceAll("_", " ");
}

function MonthlyDetails({ analytics }: { analytics: PublicAnalytics | null }) {
  return (
    <>
      <div className="analytics-grid">
        <article className="panel monthly-panel">
          <div className="panel-head">
            <div>
              <h2>月度績效</h2>
              <p>PARTIAL_INCEPTION 與 MTD 顯示實值，但不納入完整月份排名。</p>
            </div>
          </div>
          <MonthlyBarChart monthly={analytics?.monthly ?? []} />
          <div className="month-highlights">
            <MetricCard
              label="最佳完整月份"
              value={
                analytics?.monthlyOverview.bestMonth
                  ? `${analytics.monthlyOverview.bestMonth.month} · ${formatPercent(analytics.monthlyOverview.bestMonth.returnPct, true)}`
                  : "資料不足"
              }
            />
            <MetricCard
              label="最差完整月份"
              value={
                analytics?.monthlyOverview.worstMonth
                  ? `${analytics.monthlyOverview.worstMonth.month} · ${formatPercent(analytics.monthlyOverview.worstMonth.returnPct, true)}`
                  : "資料不足"
              }
            />
            <MetricCard
              label="完整月份正報酬比例"
              value={formatPercent(
                analytics?.monthlyOverview.positiveMonthRatioPct,
              )}
            />
            <MetricCard
              label="連續正／負完整月份"
              value={
                analytics
                  ? `${analytics.monthlyOverview.longestPositiveStreak} / ${analytics.monthlyOverview.longestNegativeStreak}`
                  : "N/A"
              }
            />
          </div>
        </article>
        <article className="panel heatmap-panel">
          <div className="panel-head">
            <div>
              <h2>年度 × 月份 Heatmap</h2>
              <p>部分月份顯示實際值並使用獨立角標；灰色不是 0%。</p>
            </div>
          </div>
          <PerformanceHeatmap monthly={analytics?.monthly ?? []} />
        </article>
      </div>
      <article className="panel monthly-table-panel">
        <div className="panel-head">
          <div>
            <h2>各月績效明細</h2>
            <p>月度邊界與已完成交易數一律使用 UTC。</p>
          </div>
        </div>
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>月份</th>
                <th>報酬</th>
                <th>最大回撤</th>
                <th>完成交易</th>
                <th>狀態</th>
                <th>有效期間／原因</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.monthly
                .slice()
                .reverse()
                .map((month) => (
                  <tr key={month.month}>
                    <td data-label="月份">
                      {month.month}
                      {month.status === "PARTIAL_INCEPTION" ? (
                        <small>
                          績效追蹤自{" "}
                          {formatDate(month.trackingStartAt).replaceAll(
                            "/",
                            "/",
                          )}{" "}
                          開始
                        </small>
                      ) : null}
                    </td>
                    <td
                      data-label="報酬"
                      className={
                        month.returnPct === null
                          ? ""
                          : month.returnPct >= 0
                            ? "positive-number"
                            : "negative-number"
                      }
                    >
                      {formatPercent(month.returnPct, true)}
                    </td>
                    <td data-label="最大回撤">
                      {formatPercent(month.maxDrawdownPct)}
                    </td>
                    <td data-label="完成交易">{month.tradeCount}</td>
                    <td data-label="狀態">
                      <span
                        className={`status-tag status-${month.status.toLowerCase()}`}
                      >
                        {statusLabel(month)}
                      </span>
                    </td>
                    <td data-label="說明">
                      <span className="month-range">
                        {month.status === "PARTIAL_INCEPTION" &&
                        month.trackingStartAt
                          ? `此數據僅涵蓋 ${formatDate(month.trackingStartAt)}–${month.month.replace("-", "/")}/${new Date(Date.UTC(Number(month.month.slice(0, 4)), Number(month.month.slice(5, 7)), 0)).getUTCDate()}，不是完整曆月績效。`
                          : (month.reason ??
                            `${formatDate(month.baselineAt)} → ${formatDate(month.endAt)}`)}
                      </span>
                    </td>
                  </tr>
                )) ?? (
                <tr>
                  <td colSpan={6}>資料不足</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </article>
    </>
  );
}

function PnlCards({
  equity,
  currency,
}: {
  equity: PublicEquity | null;
  currency: string;
}) {
  const pnl = equity?.pnl;
  const cards = [
    ["累計資金增加", pnl?.cumulativeCashIn, "所有外部入金與資金增加。"],
    [
      "累計資金減少",
      pnl?.cumulativeCashOut,
      "所有外部出金與資金減少，以正數呈現。",
    ],
    [
      "淨資金流",
      pnl?.netCashFlow,
      "Publisher cash_flow_total；不重複加入初始資金。",
    ],
    [
      "累計已實現交易損益",
      pnl?.realizedTradingPnl,
      "已平倉交易 Profit、Commission、Swap、Fee 的淨結果。",
    ],
    [
      "目前未實現損益",
      pnl?.unrealizedPnl,
      "目前 Equity 減去 Balance，會隨市場價格變動。",
    ],
    [
      "含浮動總交易損益",
      pnl?.totalTradingPnlIncludingFloating,
      "已實現交易損益加目前未實現損益。",
    ],
    ["目前 Balance", pnl?.balance, "目前已實現帳戶餘額。"],
    ["目前 Equity", pnl?.equity, "目前 Balance 加上浮動損益。"],
  ] as const;
  return (
    <div className="pnl-grid">
      {cards.map(([label, value, note]) => (
        <MetricCard
          key={label}
          label={label}
          value={formatMoney(value, currency)}
          note={note}
          tone={
            (value ?? 0) < 0
              ? "risk"
              : label.includes("損益") && (value ?? 0) > 0
                ? "positive"
                : "default"
          }
        />
      ))}
      {pnl ? (
        <p className={`reconciliation-note ${pnl.status.toLowerCase()}`}>
          Reconciliation：
          {pnl.status === "MATCHED"
            ? "帳戶與 deals 已核對一致"
            : pnl.status === "RECONCILED_TO_ACCOUNT_CURRENT"
              ? "以 account_current 為權威值，並保留 deals 差異供診斷"
              : "來源 deals 不足，暫以 account_current 計算"}
          。
        </p>
      ) : null}
    </div>
  );
}

function PerformanceSectionTabs({
  value,
  onChange,
}: {
  value: PerformanceSection;
  onChange: (next: PerformanceSection) => void;
}) {
  const sections: Array<[PerformanceSection, string]> = [
    ["overview", "帳戶總覽"],
    ["monthly", "月度績效"],
    ["strategies", "策略分析"],
    ["risk", "風險與資金"],
  ];
  return (
    <div className="performance-section-tabs" role="tablist" aria-label="績效分析區段">
      {sections.map(([key, label]) => (
        <button
          type="button"
          role="tab"
          aria-selected={value === key}
          className={value === key ? "active" : ""}
          key={key}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function AccountChartPanel({
  summary,
  equity,
  loading,
  mode,
  period,
  onMode,
  onPeriod,
}: {
  summary: PublicSummary | null;
  equity: PublicEquity | null;
  loading: boolean;
  mode: ChartMode;
  period: PeriodKey;
  onMode: (next: ChartMode) => void;
  onPeriod: (next: PeriodKey) => void;
}) {
  return (
    <article className="panel performance-chart-panel">
      <div className="panel-head chart-control-head">
        <div>
          <h2>{mode === "amount" ? "帳戶金額" : "現金流中性績效曲線"}</h2>
          <p>
            {mode === "amount"
              ? "真實帳戶金額；入出金以獨立事件標記。"
              : "入金與出金不會被計入獲利或虧損。"}
          </p>
        </div>
        <div className="chart-controls">
          <div className="mode-tabs" role="group" aria-label="圖表模式">
            <button
              type="button"
              className={mode === "performance" ? "active" : ""}
              onClick={() => onMode("performance")}
            >
              績效 %
            </button>
            <button
              type="button"
              className={mode === "amount" ? "active" : ""}
              onClick={() => onMode("amount")}
            >
              帳戶金額
            </button>
          </div>
          <PeriodTabs value={period} onChange={onPeriod} />
        </div>
      </div>
      {loading && !equity ? (
        <div className="chart-loading" />
      ) : (
        <AccountChart
          equity={equity}
          summary={summary}
          currency={summary?.currency ?? "USD"}
          mode={mode}
          period={period}
        />
      )}
      <div className="method-disclosure">
        <Icon name="database" />
        <p>
          績效曲線使用現金流中性報酬，入金與出金不會被計入獲利或虧損。帳戶金額模式顯示真實
          Balance／Equity 與外部資金異動；Historical Balance
          為成交紀錄重建，不代表歷史浮動 Equity。
        </p>
      </div>
    </article>
  );
}

function AccountOverviewSection({
  summary,
  equity,
  analytics,
  summaryLoading,
  equityLoading,
  period,
}: {
  summary: PublicSummary | null;
  equity: PublicEquity | null;
  analytics: PublicAnalytics | null;
  summaryLoading: boolean;
  equityLoading: boolean;
  period: PeriodKey;
}) {
  const metrics = getCanonicalAccountMetrics(summary, analytics);
  const now = useClockSeconds();
  const freshness = summary && now
    ? getFreshness(summary.lastUpdatedAt, now)
    : null;
  return (
    <section className="performance-active-section" role="tabpanel">
      <div className="metrics-grid performance-overview-metrics">
        <MetricCard label="Balance" value={formatMoney(metrics?.balance, summary?.currency ?? "USD")} />
        <MetricCard label="Equity" value={formatMoney(metrics?.equity, summary?.currency ?? "USD")} />
        <MetricCard label="累積現金流中性報酬" value={formatPercent(metrics?.cumulativeReturnPct, true)} />
        <MetricCard label="本月報酬" value={formatPercent(metrics?.currentMonthReturnPct, true)} />
        <MetricCard label="目前回撤" value={formatPercent(metrics?.currentDrawdownPct)} tone="risk" />
        <MetricCard label="最大回撤" value={formatPercent(metrics?.maximumDrawdownPct)} tone="risk" />
      </div>
      <div className="account-facts">
        <div><span>帳戶</span><strong>{summary?.accountType === "REAL" ? "Real Account" : (summary?.accountType ?? "N/A")}</strong></div>
        <div><span>資料狀態</span><strong>{freshness?.label ?? "N/A"}</strong></div>
        <div><span>最後同步</span><strong>{formatTaipeiTime(summary?.lastUpdatedAt)}</strong></div>
        <div><span>資料起始</span><strong>{formatTaipeiTime(analytics?.historyStartAt)}</strong></div>
        <div><span>第一筆實際交易</span><strong>{formatTaipeiTime(analytics?.firstActualTradeAt)}</strong></div>
      </div>
      <article className="panel home-chart">
        <div className="panel-head">
          <div><h2>精簡績效曲線</h2><p>固定使用完整帳戶 ALL 數據。</p></div>
        </div>
        {equityLoading && !equity ? (
          <div className="chart-loading" />
        ) : (
          <AccountChart
            equity={equity}
            summary={summary}
            currency={summary?.currency ?? "USD"}
            mode="performance"
            period={period}
            compact
          />
        )}
      </article>
      {summaryLoading && !summary ? <div className="chart-loading" /> : null}
    </section>
  );
}

function RiskAndCapitalSection({
  summary,
  equity,
  analytics,
  equityLoading,
  state,
  update,
}: {
  summary: PublicSummary | null;
  equity: PublicEquity | null;
  analytics: PublicAnalytics | null;
  equityLoading: boolean;
  state: PerformanceState;
  update: (patch: Partial<PerformanceState>) => void;
}) {
  return (
    <section className="performance-active-section" role="tabpanel">
      <section className="drawdown-grid">
        <article className="panel">
          <div className="panel-head"><div><h2>Drawdown Timeline</h2><p>以現金流中性績效高點計算。</p></div></div>
          <UnderwaterChart points={analytics?.drawdown.points ?? []} />
        </article>
        <div className="metrics-grid compact-metrics">
          <MetricCard label="目前回撤" value={formatPercent(analytics?.drawdown.currentDrawdownPct)} />
          <MetricCard label="最大回撤" value={formatPercent(analytics?.drawdown.maximumDrawdownPct)} />
          <MetricCard label="最長回撤期間" value={analytics?.drawdown.longestDrawdownDays == null ? "N/A" : `${analytics.drawdown.longestDrawdownDays} 天`} />
          <MetricCard label="平均恢復時間" value={analytics?.drawdown.averageRecoveryDays == null ? "N/A" : `${analytics.drawdown.averageRecoveryDays} 天`} />
        </div>
      </section>
      <section className="page-section">
        <h2>資金流與損益拆分</h2>
        <PnlCards equity={equity} currency={summary?.currency ?? analytics?.currency ?? "USD"} />
      </section>
      <AccountChartPanel
        summary={summary}
        equity={equity}
        loading={equityLoading}
        mode={state.chartMode}
        period={state.period}
        onMode={(chartMode) => update({ chartMode })}
        onPeriod={(period) => update({ period })}
      />
    </section>
  );
}

function PerformancePage({
  apiBaseUrl,
  accountId,
}: {
  apiBaseUrl: string;
  accountId: string;
}) {
  const { state, update } = usePerformanceState();
  const needsAccountData = state.section === "overview" || state.section === "risk";
  const account = useAccountData(apiBaseUrl, accountId, state.period, needsAccountData);
  const accountAnalytics = useAnalytics(apiBaseUrl, accountId, "ALL");
  const strategyAnalytics = useAnalytics(
    apiBaseUrl,
    accountId,
    state.strategy,
    state.section === "strategies" && state.strategy !== "ALL",
  );
  const selectedAnalytics = state.strategy === "ALL"
    ? accountAnalytics.analytics
    : strategyAnalytics.analytics;
  return (
    <PageShell view="performance">
      <main className="page-main">
        <div className="site-container">
          <SectionHeading
            kicker="PUBLIC PERFORMANCE HUB"
            title="完整帳戶與策略績效"
            copy="在單一績效中心切換帳戶、月份、Magic Number 策略及風險資金分析；不公開逐筆交易內容。"
          />
          <PerformanceSectionTabs
            value={state.section}
            onChange={(section) => update({
              section,
              ...(section === "risk" ? { chartMode: "amount" as ChartMode } : {}),
            })}
          />
          <DataAlert message={
            (needsAccountData ? account.error : null) ??
            accountAnalytics.error ??
            (state.section === "strategies" && state.strategy !== "ALL"
              ? strategyAnalytics.error
              : null)
          } />
          {state.section === "overview" ? (
            <AccountOverviewSection
              summary={account.summary}
              equity={account.equity}
              analytics={accountAnalytics.analytics}
              summaryLoading={account.summaryLoading}
              equityLoading={account.equityLoading}
              period={state.period}
            />
          ) : state.section === "monthly" ? (
            <section className="performance-active-section" role="tabpanel">
              <div className="section-toolbar"><PeriodTabs value={state.period} onChange={(period) => update({ period })} /></div>
              <MonthlyDetails analytics={accountAnalytics.analytics} />
            </section>
          ) : state.section === "strategies" ? (
            <StrategySection
              accountAnalytics={accountAnalytics.analytics}
              analytics={selectedAnalytics}
              loading={state.strategy === "ALL" ? accountAnalytics.loading : strategyAnalytics.loading}
              strategy={state.strategy}
              period={state.period}
              onStrategy={(strategy) => update({ strategy })}
              onPeriod={(period) => update({ period })}
            />
          ) : (
            <RiskAndCapitalSection
              summary={account.summary}
              equity={account.equity}
              analytics={accountAnalytics.analytics}
              equityLoading={account.equityLoading}
              state={state}
              update={update}
            />
          )}
          <section className="data-method">
            <h2>資料來源與計算說明</h2>
            <p>
              正式績效追蹤起點依 deals 中第一筆 BUY／SELL 的時間與 Ticket 排序判斷：
              {formatTaipeiTime(accountAnalytics.analytics?.firstActualTradeAt)}。
              基準優先使用交易前可靠 Historical Balance；不存在時由完整 deal ledger 與目前 Balance
              重建第一筆交易前本金，最後才使用日結點反推備援。所有缺失資料均顯示 N/A，不會設為 0 或以示意績效取代。
            </p>
          </section>
        </div>
      </main>
    </PageShell>
  );
}

function StatGrid({
  analytics,
  currency,
  strategyMode = false,
}: {
  analytics: PublicAnalytics | null;
  currency: string;
  strategyMode?: boolean;
}) {
  return (
    <div className="trade-stat-grid">
      <MetricCard
        label="已完成交易"
        value={analytics ? String(analytics.trades.totalTrades) : "N/A"}
      />
      <MetricCard
        label={strategyMode ? "策略淨損益貢獻" : "淨交易損益"}
        value={formatMoney(analytics?.trades.netPnl, currency)}
      />
      <MetricCard
        label="勝率"
        value={formatPercent(analytics?.trades.winRatePct)}
      />
      <MetricCard
        label="Profit Factor"
        value={analytics?.trades.profitFactor?.toFixed(2) ?? "N/A"}
      />
      <MetricCard
        label="Expectancy"
        value={formatMoney(analytics?.trades.expectancy, currency)}
      />
      <MetricCard
        label="平均獲利"
        value={formatMoney(analytics?.trades.averageWin, currency)}
      />
      <MetricCard
        label="平均虧損"
        value={formatMoney(analytics?.trades.averageLoss, currency)}
      />
      <MetricCard
        label="平均盈虧比"
        value={analytics?.trades.averagePayoffRatio?.toFixed(2) ?? "N/A"}
      />
      <MetricCard
        label={strategyMode ? "最大策略回撤貢獻" : "最大回撤"}
        value={formatPercent(analytics?.drawdown.maximumDrawdownPct)}
      />
      <MetricCard
        label="最大連勝／連敗"
        value={analytics ? `${analytics.trades.maxWinningStreak} / ${analytics.trades.maxLosingStreak}` : "N/A"}
      />
      <MetricCard
        label="平均持倉時間"
        value={formatDuration(analytics?.trades.averageHoldingSeconds)}
      />
      <MetricCard
        label="最佳交易"
        value={formatMoney(analytics?.bestTrade?.netPnl, currency)}
      />
      <MetricCard
        label="最差交易"
        value={formatMoney(analytics?.worstTrade?.netPnl, currency)}
      />
      <MetricCard
        label="統計來源 deals"
        value={analytics ? String(analytics.sourceDealCount) : "N/A"}
        note={`未配對 ${analytics?.unmatchedDealCount ?? "N/A"}`}
      />
    </div>
  );
}

function SegmentCards({
  analytics,
  currency,
}: {
  analytics: PublicAnalytics | null;
  currency: string;
}) {
  return (
    <div className="segment-grid">
      <article>
        <h3>Long／Short</h3>
        {analytics?.longShort.map((item) => (
          <div key={item.key}>
            <b>{item.key}</b>
            <span>{item.trades} 筆</span>
            <strong>{formatMoney(item.netPnl, currency)}</strong>
          </div>
        ))}
      </article>
      <article>
        <h3>商品別績效</h3>
        {analytics?.bySymbol.map((item) => (
          <div key={item.key}>
            <b>{item.key}</b>
            <span>
              {item.trades} 筆 · PF {item.profitFactor?.toFixed(2) ?? "N/A"}
            </span>
            <strong>{formatMoney(item.netPnl, currency)}</strong>
          </div>
        ))}
      </article>
      <article>
        <h3>Magic Number</h3>
        {analytics?.byMagic.map((item) => (
          <div key={item.key}>
            <b>{item.key}</b>
            <span>{item.trades} 筆</span>
            <strong>{formatMoney(item.netPnl, currency)}</strong>
          </div>
        ))}
      </article>
    </div>
  );
}

type StrategySortKey = "netPnl" | "profitFactor" | "winRatePct" | "trades" | "drawdown";

function StrategyComparison({
  analytics,
}: {
  analytics: PublicAnalytics | null;
}) {
  const [sortKey, setSortKey] = useState<StrategySortKey>("netPnl");
  const currency = analytics?.currency ?? "USD";
  const rows = [...(analytics?.byMagic ?? [])].sort((left, right) => {
    if (sortKey === "drawdown") {
      return (left.maximumDrawdownContributionPct ?? Number.POSITIVE_INFINITY) -
        (right.maximumDrawdownContributionPct ?? Number.POSITIVE_INFINITY);
    }
    const value = (row: PublicAnalytics["byMagic"][number]) =>
      sortKey === "netPnl"
        ? row.netPnl
        : sortKey === "profitFactor"
          ? (row.profitFactor ?? Number.NEGATIVE_INFINITY)
          : sortKey === "winRatePct"
            ? (row.winRatePct ?? Number.NEGATIVE_INFINITY)
            : row.trades;
    return value(right) - value(left);
  });
  const groupedNetPnl = rows.reduce((sum, row) => sum + row.netPnl, 0);
  const groupedTrades = rows.reduce((sum, row) => sum + row.trades, 0);
  const netDelta = analytics ? analytics.trades.netPnl - groupedNetPnl : null;
  const tradeDelta = analytics ? analytics.trades.totalTrades - groupedTrades : null;
  return (
    <article className="panel strategy-comparison-panel">
      <div className="panel-head">
        <div>
          <h2>策略比較總覽</h2>
          <p>所有已完成交易依 Magic Number 彙總；未設定 Magic 歸入 UNMAPPED／OTHER。</p>
        </div>
        <label className="sort-control">
          排序
          <select value={sortKey} onChange={(event) => setSortKey(event.target.value as StrategySortKey)}>
            <option value="netPnl">淨損益</option>
            <option value="profitFactor">Profit Factor</option>
            <option value="winRatePct">勝率</option>
            <option value="trades">交易數</option>
            <option value="drawdown">最大回撤貢獻</option>
          </select>
        </label>
      </div>
      <div className="strategy-comparison-grid">
        {rows.length ? rows.map((row) => (
          <article key={row.key} className="strategy-comparison-card">
            <div><strong>{strategyDisplayName(row.key)}</strong><span>{row.key === "UNMAPPED/OTHER" ? "未映射" : `Magic ${row.key}`}</span></div>
            <dl>
              <div><dt>完成交易</dt><dd>{row.trades}</dd></div>
              <div><dt>淨損益</dt><dd>{formatMoney(row.netPnl, currency)}</dd></div>
              <div><dt>勝率</dt><dd>{formatPercent(row.winRatePct)}</dd></div>
              <div><dt>PF</dt><dd>{row.profitFactor?.toFixed(2) ?? "N/A"}</dd></div>
              <div><dt>Expectancy</dt><dd>{formatMoney(row.expectancy, currency)}</dd></div>
              <div><dt>最大回撤貢獻</dt><dd>{formatPercent(row.maximumDrawdownContributionPct)}</dd></div>
              <div><dt>平均獲利</dt><dd>{formatMoney(row.averageWin, currency)}</dd></div>
              <div><dt>平均虧損</dt><dd>{formatMoney(row.averageLoss, currency)}</dd></div>
              <div><dt>平均盈虧比</dt><dd>{row.averagePayoffRatio?.toFixed(2) ?? "N/A"}</dd></div>
            </dl>
          </article>
        )) : <div className="empty-state">目前沒有可彙總的策略交易資料，不會以 0 代替。</div>}
      </div>
      <div className="strategy-reconciliation">
        <strong>ALL reconciliation</strong>
        <span>完成交易差異：{tradeDelta ?? "N/A"}</span>
        <span>淨損益差異：{formatMoney(netDelta, currency)}</span>
        <span>未配對來源 deals：{analytics?.unmatchedDealCount ?? "N/A"}</span>
      </div>
    </article>
  );
}

function StrategySection({
  accountAnalytics,
  analytics,
  loading,
  strategy,
  period,
  onStrategy,
  onPeriod,
}: {
  accountAnalytics: PublicAnalytics | null;
  analytics: PublicAnalytics | null;
  loading: boolean;
  strategy: string;
  period: PeriodKey;
  onStrategy: (next: string) => void;
  onPeriod: (next: PeriodKey) => void;
}) {
  const strategyMode = strategy !== "ALL";
  return (
    <section className="performance-active-section" role="tabpanel">
          <StrategyComparison analytics={accountAnalytics} />
          <div className="strategy-toolbar">
            <label>
              Magic Number
              <select
                value={strategy}
                onChange={(event) => onStrategy(event.target.value)}
              >
                <option value="ALL">ALL｜完整帳戶</option>
                {strategyMode &&
                !accountAnalytics?.availableStrategies.includes(strategy) ? (
                  <option value={strategy}>{strategyDisplayName(strategy)}</option>
                ) : null}
                {accountAnalytics?.availableStrategies.map((magic) => (
                  <option value={magic} key={magic}>
                    {strategyDisplayName(magic)}｜{magic}
                  </option>
                ))}
              </select>
            </label>
            <PeriodTabs value={period} onChange={onPeriod} />
          </div>
          {strategyMode ? (
            <div className="semantic-banner">
              <Icon name="shield" />
              <div>
                <strong>相對帳戶資金的策略績效貢獻</strong>
                <span>
                  個別策略分析依 Magic Number 將已完成交易分類。因策略未配置獨立資金帳戶，
                  百分比代表相對帳戶資金的績效貢獻，不等同於獨立策略 ROI；帳戶入出金不歸屬於此策略。
                </span>
              </div>
            </div>
          ) : null}
          <article className="panel">
            <div className="panel-head">
              <div>
                <h2>
                  {strategyMode
                    ? `${strategyDisplayName(strategy)} 累積損益貢獻`
                    : "ALL｜帳戶績效與策略構成"}
                </h2>
                <p>
                  {strategyMode
                    ? "以已完成交易淨損益累積，換算相對帳戶起始資金的貢獻。"
                    : "ALL 模式使用真實帳戶現金流中性績效，不把策略貢獻稱為 Balance。"}
                </p>
              </div>
            </div>
            {loading ? (
              <div className="chart-loading" />
            ) : strategyMode ? (
              <StrategyContributionChart
                analytics={analytics}
                currency={analytics?.currency ?? "USD"}
              />
            ) : (
              <div className="all-strategy-summary">
                <p>
                  選擇 Magic Number
                  可檢視策略累積損益貢獻曲線；ALL 保持完整帳戶現金流中性口徑。
                </p>
              </div>
            )}
          </article>
          <section className="page-section">
            <StatGrid
              analytics={analytics}
              currency={analytics?.currency ?? "USD"}
              strategyMode={strategyMode}
            />
          </section>
          <section className="page-section">
            <MonthlyDetails analytics={analytics} />
          </section>
          <section className="page-section">
            <SegmentCards
              analytics={analytics}
              currency={analytics?.currency ?? "USD"}
            />
          </section>
          {loading ? <div className="chart-loading" /> : null}
          <p className="aggregate-only-note">公開頁面僅呈現彙總指標，不顯示 Ticket 或逐筆交易內容；後端 deals 與交易配對仍用於所有統計。</p>
    </section>
  );
}

function PublicRecordCard({
  title,
  url,
  label,
  copy,
}: {
  title: string;
  url: string;
  label: string;
  copy: string;
}) {
  return (
    <article className="verification-card">
      <div>
        <span className="verification-monogram">
          {title.slice(0, 2).toUpperCase()}
        </span>
        <span className="verification-state">{label}</span>
      </div>
      <h2>{title}</h2>
      <p>{copy}</p>
      <a href={url} target="_blank" rel="noreferrer">
        開啟公開績效頁 <Icon name="external" />
      </a>
    </article>
  );
}

function VerificationPage({
  apiBaseUrl,
  accountId,
}: {
  apiBaseUrl: string;
  accountId: string;
}) {
  const { summary, error } = useAccountData(apiBaseUrl, accountId, "1y");
  const [health, setHealth] = useState<{
    ok: boolean;
    timestamp: number;
  } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    getJson<{ database: string; timestamp: number }>(
      joinUrl(apiBaseUrl, "/api/v1/health"),
      controller.signal,
    )
      .then((data) =>
        setHealth({ ok: data.database === "ok", timestamp: data.timestamp }),
      )
      .catch(() =>
        setHealth({ ok: false, timestamp: Math.floor(Date.now() / 1000) }),
      );
    return () => controller.abort();
  }, [apiBaseUrl]);
  return (
    <PageShell view="verification">
      <main className="page-main">
        <div className="site-container">
          <SectionHeading
            kicker="PUBLIC RECORDS"
            title="第三方公開績效與資料說明"
            copy="公開網址表示已連結第三方公開追蹤頁，不代表本站或交易系統已取得第三方正式認證。"
          />
          <DataAlert message={error} />
          <div className="verification-grid">
            <PublicRecordCard
              title="SignalStart"
              url={SIGNALSTART_URL}
              label="已連結公開紀錄"
              copy="第三方公開追蹤頁；更新頻率與統計口徑依 SignalStart 頁面為準。"
            />
            <PublicRecordCard
              title="Myfxbook"
              url={MYFXBOOK_URL}
              label="第三方公開追蹤頁"
              copy="公開績效頁；本站不以網址存在宣稱已取得正式認證。"
            />
            <article className="verification-card">
              <div>
                <span className="verification-monogram live">API</span>
                <StatusPill
                  level={health?.ok ? "live" : health ? "offline" : "neutral"}
                  label={
                    health?.ok
                      ? "Operational"
                      : health
                        ? "Unavailable"
                        : "Checking"
                  }
                />
              </div>
              <h2>Worker／D1</h2>
              <p>
                公開唯讀 API 與資料庫狀態；不等於券商、VPS 或 MT5 帳戶連線狀態。
              </p>
              <span>檢查時間 {formatTaipeiTime(health?.timestamp)}</span>
              <span>最後同步 {formatTaipeiTime(summary?.lastUpdatedAt)}</span>
            </article>
          </div>
          <div className="explanation-grid">
            <article>
              <h2>資料來源</h2>
              <p>
                Publisher 每 60 秒提供 Live Balance／Equity；deals
                用於完成交易配對與 Historical Balance 重建。
              </p>
            </article>
            <article>
              <h2>Historical Balance</h2>
              <p>
                由實際成交紀錄重建日結 Balance，不是歷史浮動
                Equity，也不憑空填補無資料日期。
              </p>
            </article>
            <article>
              <h2>現金流中性報酬</h2>
              <p>
                以 performance index 比值計算，外部入金、出金、Credit 與 Balance
                事件不會成為交易報酬。
              </p>
            </article>
            <article>
              <h2>風險揭露</h2>
              <p>{siteConfig.riskDisclosure}</p>
            </article>
          </div>
        </div>
      </main>
    </PageShell>
  );
}

const policyContent = {
  privacy: [
    "PRIVACY",
    "隱私權政策",
    [
      [
        "V5.5.0 Phase 1 · 2026-08-07",
        "新增 LINE Messaging API 核心通知基礎建設：安全群組綁定、交易事件、30 分鐘摘要、日／週／月 frozen snapshot、重試與受保護的 Admin API；圖片報表留待 Phase 2。",
      ],
      [
        "資料最小化",
        "本站公開頁只使用自訂 account_id、帳戶類型、幣別、績效 Snapshot 與選擇性上傳的交易資料；不要求或儲存 MT5 密碼、完整帳號或客戶姓名。",
      ],
      [
        "外部連結",
        "SignalStart、Myfxbook 與聯絡連結由第三方提供，其資料處理由各平台負責。",
      ],
    ],
  ],
  terms: [
    "TERMS",
    "使用條款",
    [
      [
        "資訊用途",
        "本站內容用於介紹 EA、公開績效與技術運作，不構成投資建議、代操、招攬或保證。",
      ],
      [
        "使用責任",
        "使用者應自行確認券商規則、帳戶條件、風險承受度與法令要求。",
      ],
    ],
  ],
  risk: [
    "RISK DISCLOSURE",
    "風險揭露",
    [
      ["槓桿風險", siteConfig.riskDisclosure],
      [
        "執行差異",
        "點差、滑點、延遲、流動性、跳空、券商設定、商品規格與伺服器時間，皆可能使實際結果與回測或其他帳戶不同。",
      ],
    ],
  ],
  changelog: [
    "CHANGELOG",
    "版本更新紀錄",
    [
      [
        "V5.4.1 · 2026-08-06",
        "以 deal ledger 修正同日初始入金的交易前本金，將帳戶、月度、Magic Number 策略與風險資金分析合併至單一績效中心，並移除公開逐筆交易 UI。",
      ],
      [
        "V5.4.0 · 2026-08-06",
        "整合首次部分月份、多頁路由、策略與交易 URL 狀態、損益 reconciliation、帳戶金額圖表、Hero 與手機版體驗。",
      ],
      [
        "V5.1.0 · 2026-08-01",
        "修正 UTC 月界、稀疏月份、MTD 排名、Heatmap 與已完成交易口徑。",
      ],
      [
        "V5.0.0 · 2026-08-01",
        "建立品牌、公開績效中心、產品、法律頁與 Cloudflare 架構。",
      ],
    ],
  ],
} as const;

function StaticPage({
  view,
  apiBaseUrl,
}: {
  view: "privacy" | "terms" | "risk" | "changelog" | "status" | "not-found";
  apiBaseUrl: string;
}) {
  const [health, setHealth] = useState<{
    ok: boolean;
    timestamp: number;
  } | null>(null);
  useEffect(() => {
    if (view !== "status") return;
    const controller = new AbortController();
    getJson<{ database: string; timestamp: number }>(
      joinUrl(apiBaseUrl, "/api/v1/health"),
      controller.signal,
    )
      .then((data) =>
        setHealth({ ok: data.database === "ok", timestamp: data.timestamp }),
      )
      .catch(() =>
        setHealth({ ok: false, timestamp: Math.floor(Date.now() / 1000) }),
      );
    return () => controller.abort();
  }, [apiBaseUrl, view]);
  if (view === "not-found")
    return (
      <PageShell view={view}>
        <main className="standalone-shell">
          <article className="standalone-card">
            <p className="eyebrow">404</p>
            <h1>找不到這個頁面</h1>
            <a className="button" href="/">
              返回首頁
            </a>
          </article>
        </main>
      </PageShell>
    );
  if (view === "status")
    return (
      <PageShell view={view}>
        <main className="standalone-shell">
          <article className="standalone-card">
            <p className="eyebrow">SYSTEM STATUS</p>
            <h1>網站健康狀態</h1>
            <div className="status-summary">
              <StatusPill
                level={health?.ok ? "live" : health ? "offline" : "neutral"}
                label={
                  health?.ok
                    ? "Operational"
                    : health
                      ? "Unavailable"
                      : "Checking"
                }
              />
              <div>
                <strong>Cloudflare Worker API／D1</strong>
                <span>{formatTaipeiTime(health?.timestamp)}</span>
              </div>
            </div>
            <p>此狀態不等於券商、MT5 或 VPS 狀態。</p>
          </article>
        </main>
      </PageShell>
    );
  const content = policyContent[view];
  return (
    <PageShell view={view}>
      <main className="standalone-shell">
        <article className="standalone-card">
          <p className="eyebrow">{content[0]}</p>
          <h1>{content[1]}</h1>
          <p className="policy-updated">更新日期：{siteConfig.updatedAt}</p>
          <div className="policy-sections">
            {content[2].map(([title, copy]) => (
              <section key={title}>
                <h2>{title}</h2>
                <p>{copy}</p>
              </section>
            ))}
          </div>
        </article>
      </main>
    </PageShell>
  );
}

function StructuredData() {
  const product = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: siteConfig.eaName,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Windows with MetaTrader 5",
    softwareVersion: siteConfig.version,
    offers: {
      "@type": "Offer",
      priceCurrency: siteConfig.currency,
      price: siteConfig.price,
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(product) }}
    />
  );
}

export function Dashboard({
  apiBaseUrl = siteConfig.workerUrl,
  accountId = siteConfig.publicAccountId,
  view = "home",
}: DashboardProps) {
  const resolvedApi = apiBaseUrl || siteConfig.workerUrl;
  const resolvedAccount = accountId || siteConfig.publicAccountId;
  const content =
    view === "home" ? (
      <HomePage apiBaseUrl={resolvedApi} accountId={resolvedAccount} />
    ) : view === "performance" ? (
      <PerformancePage apiBaseUrl={resolvedApi} accountId={resolvedAccount} />
    ) : view === "verification" ? (
      <VerificationPage apiBaseUrl={resolvedApi} accountId={resolvedAccount} />
    ) : (
      <StaticPage view={view} apiBaseUrl={resolvedApi} />
    );
  return (
    <ErrorBoundary>
      <StructuredData />
      {content}
    </ErrorBoundary>
  );
}
